import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface ImageCropper23Props {
  src: string;
  width?: number; // canvas width, default 200
  height?: number; // canvas height, default 300
  onCancel: () => void;
  onSave: (blob: Blob) => void;
}

const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

const ImageCropper23: React.FC<ImageCropper23Props> = ({ src, width = 200, height = 300, onCancel, onSave }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [lastPos, setLastPos] = useState<{ x: number; y: number } | null>(null);
  const [scale, setScale] = useState(1);
  const [baseScale, setBaseScale] = useState(1);
  const [offset, setOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const scaledW = img.width * scale;
    const scaledH = img.height * scale;
    // Center of canvas
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    // Top-left of image after applying offset
    const dx = cx - scaledW / 2 + offset.x;
    const dy = cy - scaledH / 2 + offset.y;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, dx, dy, scaledW, scaledH);
  }, [scale, offset]);

  // Load image
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imgRef.current = img;
      // Compute base scale to ensure the image covers the crop area (cover)
      const sx = width / img.width;
      const sy = height / img.height;
      const coverScale = Math.max(sx, sy);
      setBaseScale(coverScale);
      setScale(coverScale);
      setOffset({ x: 0, y: 0 });
    };
    img.src = src;
  }, [src, width, height]);

  useEffect(() => {
    draw();
  }, [draw]);

  // Constrain offsets so the image always covers the canvas
  const clampOffset = useCallback((nextOffset: { x: number; y: number }, nextScale: number) => {
    const img = imgRef.current;
    if (!img) return nextOffset;
    const scaledW = img.width * nextScale;
    const scaledH = img.height * nextScale;
    const maxX = Math.max(0, (scaledW - width) / 2);
    const maxY = Math.max(0, (scaledH - height) / 2);
    return { x: clamp(nextOffset.x, -maxX, maxX), y: clamp(nextOffset.y, -maxY, maxY) };
  }, [width, height]);

  const handleMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setLastPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!isDragging || !lastPos) return;
    const dx = e.clientX - lastPos.x;
    const dy = e.clientY - lastPos.y;
    const next = clampOffset({ x: offset.x + dx, y: offset.y + dy }, scale);
    setOffset(next);
    setLastPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp: React.MouseEventHandler<HTMLDivElement> = () => {
    setIsDragging(false);
    setLastPos(null);
  };

  const handleLeave: React.MouseEventHandler<HTMLDivElement> = () => {
    setIsDragging(false);
    setLastPos(null);
  };

  const minScale = baseScale;
  const maxScale = baseScale * 3;

  const handleZoomChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const nextScale = clamp(parseFloat(e.target.value), minScale, maxScale);
    // keep same focal point; just clamp offset for new scale
    const nextOffset = clampOffset(offset, nextScale);
    setScale(nextScale);
    setOffset(nextOffset);
  };

  const save = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (blob) onSave(blob);
    }, 'image/png');
  };

  const overlayStyles = useMemo<React.CSSProperties>(() => ({
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.55)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16
  }), []);

  const panelStyles = useMemo<React.CSSProperties>(() => ({
    background: '#fff',
    borderRadius: 'var(--radius)',
    padding: 16,
    boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
    width: 'min(95vw, 520px)'
  }), []);

  const canvasBoxStyles = useMemo<React.CSSProperties>(() => ({
    width,
    height,
    borderRadius: 'var(--radius)',
    border: '1px solid #e5e5e5',
    overflow: 'hidden',
    cursor: isDragging ? 'grabbing' : 'grab',
    touchAction: 'none',
    background: '#fafafa',
    margin: '0 auto'
  }), [width, height, isDragging]);

  return (
    <div style={overlayStyles} role="dialog" aria-modal="true">
      <div style={panelStyles}>
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>Adjust thumbnail (2:3)</h3>
        <div
          ref={containerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleLeave}
          style={canvasBoxStyles}
        >
          <canvas ref={canvasRef} width={width} height={height} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <label style={{ whiteSpace: 'nowrap' }}>Zoom</label>
          <input
            type="range"
            min={minScale}
            max={maxScale}
            step={Math.max(0.01, (maxScale - minScale) / 200)}
            value={scale}
            onChange={handleZoomChange}
            style={{ flex: 1 }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn-success" onClick={save}>Save</button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper23;


